import AsyncStorage from '@react-native-async-storage/async-storage';
import { BaseService, ClientError, ExtendableError, GenericPaginatedParameters, NotFound, Unauthorized, UnexpectedError } from './central-api/base-service';
import { PaginatedResource } from '../typings/Paginated';

interface AuthenticateParams {
    /**
     * user email or registry
     */
    username: string;
    /**
     * user password
     */
    password: string;
}

interface RegisterUserDeviceParams {
    /**
     * the unique firebase token generated by firebase/messaging
     */
    firebaseToken: string;
}


interface UpdateOwnUserParams {
    name?: string;
    email?: string;
    registry?: string;
    document?: string;
    warName?: string;
    phone?: string;
    oldPassword?: string;
    password?: string;
    confirmPassword?: string;
}

export interface CalendarEvents {
    workingIntervals?: {
        /**
        * A String containing a date on ISO format
        */
        startDate: string;
        /**
        * A String containing a date on ISO format
        */
        endDate: string;
    }[];
    deacs?: {
        title?: string;
        /**
        * A String containing a date on ISO format
        */
        startDate: string;
        /**
        * A String containing a date on ISO format
        */
        endDate: string;
    }[];
    holidays?: {
        title?: string;
        /**
        * date on format YYYY-MM-DD. Ex: 2025-12-25
        */
        startDate: string;
        /**
        * date on format YYYY-MM-DD HH:mm. Ex: 2025-12-25 16:00:00
        */
        endDate: string;
        allDay: boolean;
    }[];
    vacation?: {
        /**
        * A String containing a date on ISO format
        */
        startDate: string;
        /**
        * A String containing a date on ISO format
        */
        endDate: string;
    }[];
    absence?: {
        /**
        * A String containing a date on ISO format
        */
        startDate: string;
        /**
        * A String containing a date on ISO format
        */
        endDate: string;
    }[];
    beforeAfterIntervals?: {
        /**
        * A String containing a date on ISO format
        */
        startDate: string;
        /**
        * A String containing a date on ISO format
        */
        endDate: string;
    }[];
}

interface UpdatePictureParams {
    userId: number;
    photo: File;
}

interface PingResult {
    latency: number;
    backendTime: number;
}

export class IpAddressNotAllowed extends ExtendableError {
    ipAddress: string | undefined;
    constructor(code: string, ipAddress: string) {
        super(code);
        this.ipAddress = ipAddress;
    }
}

export class TooManyLoginsAttempts extends ExtendableError {
    lastAttempt: Date;
    waitTime: number;
    constructor(code: string, lastAttempt: Date, waitTime: number) {
        super(code);
        this.lastAttempt = lastAttempt;
        this.waitTime = waitTime;
    }
}

export class TooManyCodesRequested extends ExtendableError {
    constructor(code: string) {
        super(code);
    }
}

export class TooManyValidationsRequest extends ExtendableError {
    constructor(code: string) {
        super(code);
    }
}

export class WrongTypedCode extends ExtendableError {
    constructor(code: string) {
        super(code);
    }
}

class CentralAPI extends BaseService {

    /**
     * Authenticates user
     * @throws {Error} Will throw an error if there's something invalid on authentication
     */
    async authenticate({ username, password }: AuthenticateParams): Promise<undefined | TwoFactorUserData> {
        const res = await fetch(`${this.centralEndpoint}/authenticate`, {
            method: 'POST',
            headers: this.getHeaders(null, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                'username': username,
                'password': password
            }),
            signal: AbortSignal.timeout(30000)
        });

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'IpAddressNotAllowed') {
                throw new IpAddressNotAllowed(resJson.code, resJson.ipAddress);
            }
            if (resJson.code == 'TooManyLoginsAttempts') {
                throw new TooManyLoginsAttempts(resJson.code, resJson.createdAt, resJson.waitTime);
            }
            if (resJson.code == 'TooManyCodesRequested') {
                throw new TooManyCodesRequested(resJson.code);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        const json_response = await res.json();
        const token = json_response.token;

        if (token) {
            await AsyncStorage.setItem('BEARER_TOKEN', token);
            return;
        }
        return json_response;
    }

    /**
     * Resend the two factor code via SMS again.
     * @throws {Error}
     */
    async resendTwoFactorCode({ username, password }: AuthenticateParams): Promise<void> {
        const res = await fetch(`${this.centralEndpoint}/resend-two-factor-code`, {
            method: 'POST',
            headers: this.getHeaders(null, 'application/json;charset=utf-8'),
            body: JSON.stringify({ username, password }),
            signal: AbortSignal.timeout(30000)
        });

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'TooManyCodesRequested') {
                throw new TooManyCodesRequested(resJson.code);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    /**
    * Validate the two factor code to login the user
    * @throws {Error} Will throw an error if there's something invalid
    */
    async twoFactorValidator({ username, password, code }: AuthenticateParams & { code: string; }) {
        const res = await fetch(`${this.centralEndpoint}/validate-two-factor-code`, {
            method: 'POST',
            headers: this.getHeaders(null, 'application/json;charset=utf-8'),
            body: JSON.stringify({ username, password, code }),
            signal: AbortSignal.timeout(30000)
        });

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'TooManyValidationsRequest') {
                throw new TooManyValidationsRequest(resJson.code);
            }
            if (resJson.code == 'WrongTypedCode') {
                throw new WrongTypedCode(resJson.code);
            }
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        const json_response = await res.json();
        const token = json_response.token;

        if (token) {
            await AsyncStorage.setItem('BEARER_TOKEN', token);
        }
    }

    /**
     * Log outs the user
     * @throws {Error} Will throw an error if there's something invalid on authentication
     */
    async logout() {
        const token = await this.getToken();

        await fetch(`${this.centralEndpoint}/logout`, {
            method: 'GET',
            headers: this.getHeaders(token)
        });
        if (window.AndroidBridge && window.AndroidBridge.logout) {
            window.AndroidBridge.logout();
        }

        await AsyncStorage.removeItem('BEARER_TOKEN');
    }

    /**
     * @returns Returns if there's a bearer token stored on cache.
     */
    async isAuthenticated() {
        try {
            const res = await this.checkAuthentication();
            return !isNaN(res.id);
        } catch (e) {
            if (e instanceof Unauthorized) {
                return false;
            }
            throw e;
        }

    }

    //These 3 private variables are to avoid spamming the backend.
    //If there is currently a request being made
    //we just wait for the first one to end and return the same response to all the callers.
    //Once there is no request being made, we can start a new one when called.
    private isCheckReqOngoing: boolean = false;
    private lastRes: Promise<AuthenticatedUser> | null = null;
    private pendingResolves: ((value: AuthenticatedUser) => void)[] = [];

    async checkAuthentication(): Promise<AuthenticatedUser> {

        if (!this.isCheckReqOngoing) {
            this.isCheckReqOngoing = true;
        } else {
            if (this.lastRes) {
                return this.lastRes;
            } else {
                const lastRes = await new Promise<AuthenticatedUser>((resolve) => {
                    this.pendingResolves.push(resolve);
                });
                return lastRes;
            }
        }

        try {
            const token = await this.getToken();

            const res = await fetch(`${this.centralEndpoint}/authentication/check`, {
                method: 'GET',
                headers: this.getHeaders(token)
            });

            if (res.status != 200) {
                if (window.AndroidBridge && window.AndroidBridge.logout) {
                    window.AndroidBridge.logout();
                }

                if (res.status == 401) {
                    throw new Unauthorized();
                }
                throw new UnexpectedError();
            }

            const resp = await res.json();
            if (window.AndroidBridge && window.AndroidBridge.check && token) {
                window.AndroidBridge.check(token);
            }

            this.pendingResolves.forEach(resolve => resolve(resp));
            this.pendingResolves = [];

            return resp;
        } catch (error) {
            this.lastRes = null;
            this.pendingResolves = [];
            throw error;
        } finally {
            this.isCheckReqOngoing = false;
        }
    }

    /**
     * @returns Returns the registered token on cache.
     */
    async getToken() {
        return await AsyncStorage.getItem('BEARER_TOKEN');
    }

    /**
     * Registers the device so that it can be able to receive notifications
     * @throws {Error} Will throw an error if there's something invalid on authentication
     */
    async registerUserDevice({ firebaseToken }: RegisterUserDeviceParams) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/register-device`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                firebaseToken: firebaseToken,
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 201) {
            throw new UnexpectedError();
        }
    }

    async getTermsOfUse(): Promise<{ termsOfUse: string; }> {
        const res = await fetch(`${this.centralEndpoint}/terms-of-use`, {
            method: 'GET',
            headers: this.getHeaders(null, 'application/json;charset=utf-8')
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async acceptTermsOfUse() {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/terms-of-use/accept`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getDistanceTime(origin: LatLng, destination: LatLng): Promise<number> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/time-distance-between-points`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                originLat: origin.lat,
                originLng: origin.lng,
                destLat: destination.lat,
                destLng: destination.lng
            })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        const json = await res.json();

        const seconds = json.rows[0]?.elements[0]?.duration?.value || 0;
        return Math.ceil(seconds / 60);
    }

    /**
     * Deletes the registry of device so that it won't receive any notifications
     * @throws {Error} Will throw an error if there's something invalid on authentication
     */
    async unregisterUserDevice({ firebaseToken }: { firebaseToken: string; }) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/register-device/${firebaseToken}`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getPlaceByAddress(address: string) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/place-by-address`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ address })
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAddressByPlace(params: { latitude: number; longitude: number; getFullObject?: boolean; }): Promise<string> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/address-by-place${this.encodeQueryParams(params)}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getFullPlaceByAddress(params: { address: string; getFullObject?: boolean; }): Promise<string> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/full-place-by-address${this.encodeQueryParams(params)}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }


    async getOwnUser(params: { includeRole: boolean; }): Promise<UserData> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/user/me${this.encodeQueryParams(params)}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    /**
     *
     * @param params
     * @throws {ClientError} EmailAlreadyExists, InvalidOldPassword, PasswordsDoNotMatch, UnmatchedPasswordRequirements
     * @returns
     */
    async updateOwnUser(params: UpdateOwnUserParams): Promise<UserData> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/user/me`, {
            method: 'PUT',
            body: JSON.stringify(params),
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const response = await res.json();
            throw new ClientError(response.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOwnUserNotes(): Promise<{ note: string; }> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/user/me/notes`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updateOwnUserNotes(note: string) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/user/me/notes`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ note })
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }

    }

    async getCalendarEvents(params: { startDate: number; endDate: number; }): Promise<CalendarEvents> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/user/me/calendar${this.encodeQueryParams(params)}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }


    async updateUserPicture({ photo, userId }: UpdatePictureParams): Promise<void> {
        const token = await this.getToken();

        const body = new FormData();

        body.append('faceImage', photo);

        const res = await fetch(`${this.centralEndpoint}/user/${userId}/facePicture`, {
            method: 'PUT',
            headers: this.getHeaders(token),
            body
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status >= 400 && res.status <= 499) {
            const response = await res.json();
            throw new ClientError(response.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return;
    }

    async getOwnUserSessions(): Promise<Session[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/user/me/sessions`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deleteOwnUserSession(sessionId: number) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/user/me/session/${sessionId}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async saveLocationToken() {
        try {
            const token = await this.getToken();

            if (!this.isAuthenticated()) {
                return;
            }

            //FIXME: REMOVE THE LOCATION TOKEN WHEN ALL ANDROID UPDATE TO VERSION 4.0.0.0
            const locationToken = await AsyncStorage.getItem('LOCATION_TOKEN');

            if (locationToken) {
                const res = await fetch(`${this.centralEndpoint}/native-app/save-location-token`, {
                    method: 'POST',
                    body: JSON.stringify({ token: locationToken }),
                    headers: this.getHeaders(token, 'application/json;charset=utf-8'),
                });

                if (res.status == 401) {
                    throw new Unauthorized();
                }

                if (res.status != 200) {
                    throw new UnexpectedError();
                }

                return res.json();
            }
        } catch (err) {
            console.error(err);
            return;
        }
    }

    async forgotPassword(email: string) {
        const res = await fetch(`${this.centralEndpoint}/user/forgot-password`, {
            method: 'POST',
            body: JSON.stringify({ email }),
            headers: this.getHeaders(null, 'application/json;charset=utf-8'),
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async resetPassword(password: string, confirmPassword: string, token: string) {
        const res = await fetch(`${this.centralEndpoint}/user/reset-password`, {
            method: 'POST',
            body: JSON.stringify({ password, confirmPassword, token }),
            headers: this.getHeaders(null, 'application/json;charset=utf-8'),
        });

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status == 406) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async createOwnSupportTicket(params: { subject: string; comment: string; }) {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/user/me/support-ticket`, {
            method: 'POST',
            body: JSON.stringify(params),
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 404) {
            throw new NotFound();
        }

        if (res.status == 406) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async getOwnSupportTickets({ page, limit }: GenericPaginatedParameters): Promise<PaginatedResource<SupportTicket>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/user/me/support-tickets${this.encodeQueryParams({ page, limit })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOwnSupportTicketById(ticketId: number): Promise<SupportTicket> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/user/me/support-ticket/${ticketId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOwnSupportTicketComments(ticketId: number): Promise<PaginatedResource<SupportTicketComment>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/user/me/support-ticket/${ticketId}/comments`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getTicketUser(ticketId: number, authorId: number): Promise<SupportTicketUser> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/user/me/support-ticket/${ticketId}/author/${authorId}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();

    }

    async replySupportTicket(params: {
        ticketId: number;
        body: string;
        files?: File[];
    }): Promise<void> {
        const token = await this.getToken();
        const formData = new FormData();
        if (params.files) {
            for (const file of params.files) {
                formData.append('files', file);
            }
        }
        formData.append('textField', params.body);

        const res = await fetch(
            `${this.centralEndpoint}/user/me/support-ticket/${params.ticketId}`,
            {
                method: 'PUT',
                headers: this.getHeaders(token),
                body: formData
            }
        );

        if (res.status === 401) {
            throw new Unauthorized();
        }

        if (res.status !== 200) {
            throw new UnexpectedError();
        }
    }

    async getFrequentlyAskedQuestions(): Promise<FrequentlyAskedQuestion[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/frequently-asked-questions`, {
            method: 'GET',
            headers: this.getHeaders(token)
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async ping(): Promise<PingResult> {
        const start = performance.now();

        const response = await fetch(`${this.centralEndpoint}/diagnostics/ping`, {
            cache: 'no-store',
        });

        await response.json();

        const latency = performance.now() - start;

        const backendTime = Number(response.headers.get('x-backend-time') ?? 0);

        return {
            latency,
            backendTime,
        };
    }

    async diagnosticsDownload(size: '1mb' | '10mb') {
        const start = performance.now();

        const response = await fetch(`${this.centralEndpoint}/diagnostics/${size}?t=${Date.now()}`, {
            cache: 'no-store',
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();

        const ms = performance.now() - start;

        const bytes = blob.size;

        const mbps = (bytes * 8) / 1024 / 1024 / (ms / 1000);

        return {
            bytes,
            ms,
            mbps,
        };
    }
}

export const centralAPI = new CentralAPI();
